// ./src/components/generic/LoadMoreButton/LoadMoreButton.tsx
//
// Reusable "Load more" button for paginated lists.
// Renders nothing when there are no more items, when the list is in its
// initial loading state, or when no onLoadMore callback is provided.
//
// Usage:
//   <LoadMoreButton
//     hasMore={hasMore}
//     loading={loading}
//     loadingMore={loadingMore}
//     onLoadMore={onLoadMore}
//   />

import React from "react";
import { Center } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { AppButton } from "@/components/generic/AppButton";

// ============================================
// Types
// ============================================

export interface LoadMoreButtonProps {
  /** Whether there are more items available to load */
  hasMore: boolean;

  /** Whether the list is in its initial loading state (hides the button) */
  loading: boolean;

  /** Whether a "load more" request is currently in flight */
  loadingMore: boolean;

  /** Callback to load the next page. Button is hidden when undefined. */
  onLoadMore?: () => void;

  /** Override the default label (default: "Load more") */
  label?: string;
}

// ============================================
// Component
// ============================================

const LoadMoreButtonComponent: React.FC<LoadMoreButtonProps> = ({
  hasMore,
  loading,
  loadingMore,
  onLoadMore,
  label,
}) => {
  const { t } = useTranslation(["common"]);

  if (!hasMore || loading || !onLoadMore) return null;

  return (
    <Center p="md">
      <AppButton
        variant="secondary"
        onClick={onLoadMore}
        inProgress={loadingMore}
      >
        {label ?? t("common:buttons.loadMore", { defaultValue: "Load more" })}
      </AppButton>
    </Center>
  );
};

export const LoadMoreButton = React.memo(LoadMoreButtonComponent);